/**
 * useAutoReload — detects new deployments and prompts the user to reload.
 *
 * How it works:
 *  1. On mount, fetches /api/version to capture the initial build hash.
 *  2. Polls every 30 seconds. If the hash changes, shows a persistent styled
 *     info toast with a "Reload Now" button — no manual refresh needed.
 *  3. The toast is shown only once per session (dismissed after reload).
 */

import { useEffect, useRef } from "react";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 30_000;

async function fetchBuildHash(): Promise<string | null> {
  try {
    const res = await fetch("/api/version", { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json() as { hash: string };
    return data.hash ?? null;
  } catch {
    return null;
  }
}

export function useAutoReload() {
  const initialHash = useRef<string | null>(null);
  const toastShown = useRef(false);

  useEffect(() => {
    fetchBuildHash().then(hash => {
      initialHash.current = hash;
    });

    const interval = setInterval(async () => {
      if (toastShown.current) return;
      const current = await fetchBuildHash();
      if (!current || !initialHash.current) return;
      if (current !== initialHash.current) {
        toastShown.current = true;
        toast.info("APX WorkCenter has been updated", {
          description: "A new version is available. Reload to get the latest features and fixes.",
          duration: Infinity,
          action: {
            label: "Reload Now",
            onClick: () => window.location.reload(),
          },
          cancel: {
            label: "Later",
            onClick: () => { toastShown.current = false; },
          },
          style: {
            background: "oklch(0.98 0.02 75)",
            border: "1px solid oklch(0.72 0.14 75)",
            color: "oklch(0.18 0.04 240)",
          },
        });
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);
}
